import { useState } from "react";
import CrudPage from "@/components/app/CrudPage";
import api, { errMsg, formatTL } from "@/lib/api";
import { FileSpreadsheet, X } from "lucide-react";
import { toast } from "sonner";

export default function CompaniesPage() {
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const runPreview = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("preview", "true");
    setLoading(true);
    try {
      const { data } = await api.post("/companies/upload-cari-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`${data.totals?.invoice_count || 0} fatura + ${data.totals?.payment_count || 0} ödeme okundu`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const runCommit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("preview", "false");
    setLoading(true);
    try {
      const { data } = await api.post("/companies/upload-cari-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = `${data.company?.name}: ${data.invoices_inserted} fatura, ${data.payments_inserted} ödeme eklendi` +
        (data.company_created ? " (yeni cari)" : "");
      toast.success(msg);
      setShowUpload(false);
      setFile(null);
      setPreview(null);
      setReloadTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  const uploadBtn = (
    <button
      data-testid="companies-cari-upload-btn"
      onClick={() => { setPreview(null); setFile(null); setShowUpload(true); }}
      className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors"
    >
      <FileSpreadsheet className="w-3.5 h-3.5" /> Cari Excel Yükle
    </button>
  );

  return (
    <>
      <CrudPage
        key={reloadTick}
        title="Firmalar"
        subtitle="Pazarlama departmanı için firma veritabanı ve müşteri adayları. Cari Excel ile kesilen fatura + gelen ödemeleri toplu yükleyebilirsiniz."
        endpoint="/companies"
        testidPrefix="companies"
        extraActions={uploadBtn}
        fields={[
          { key: "name", label: "Firma Adı", type: "text", required: true },
          { key: "sector", label: "Sektör", type: "text" },
          { key: "contact_person", label: "Yetkili Kişi", type: "text" },
          { key: "phone", label: "Telefon", type: "text" },
          { key: "email", label: "Email", type: "text" },
          { key: "address", label: "Adres", type: "text" },
          { key: "tax_office", label: "Vergi Dairesi", type: "text" },
          { key: "tax_no", label: "Vergi No", type: "text" },
          { key: "payment_terms", label: "Vade (ör. 5 GÜN)", type: "text" },
          { key: "status", label: "Durum", type: "select",
            options: [
              { value: "lead", label: "Aday" },
              { value: "prospect", label: "Görüşme" },
              { value: "active", label: "Aktif Müşteri" },
              { value: "lost", label: "Kaybedildi" },
            ] },
          { key: "notes", label: "Notlar", type: "textarea" },
        ]}
        columns={[
          { key: "name", label: "Firma" },
          { key: "tax_office", label: "VD" },
          { key: "tax_no", label: "VD No" },
          { key: "contact_person", label: "Yetkili" },
          { key: "phone", label: "Telefon" },
          { key: "payment_terms", label: "Vade" },
          { key: "status", label: "Durum" },
        ]}
      />

      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım</div>
                <div className="font-heading font-bold text-lg">Firma Cari Excel Yükleme</div>
              </div>
              <button data-testid="cari-close-btn" onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700 leading-relaxed">
                <div className="font-semibold text-[#0A0A0A] mb-2">📋 Beklenen Excel formatı:</div>
                <div className="space-y-1">
                  <div><span className="font-semibold">Üst kısım (B/C sütunları):</span> FİRMA · ADRES · VD · VD NO · YETKİLİ KİŞİ · TELEFON · MAIL · VADE</div>
                  <div><span className="font-semibold">Tablo:</span> TARİH · FATURA · FATURA NO · ÖDEME · AÇIKLAMA</div>
                </div>
                <div className="mt-2 text-neutral-500">Sistem: Firmayı otomatik oluşturur/günceller. FATURA&gt;0 satırlar <span className="font-semibold">Satış Faturası</span>, ÖDEME&gt;0 satırlar <span className="font-semibold">Gelen Ödeme</span> olur (AÇIKLAMA = banka/ödeme yolu). Mükerrer fatura no ve aynı tarih+tutardaki ödemeler atlanır.</div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                <input data-testid="cari-file-input" type="file" accept=".xlsx" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); }} className="input mt-1.5" />
              </div>

              {!preview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button data-testid="cari-preview-btn" onClick={runPreview} disabled={loading || !file}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40">
                    {loading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {preview && (
                <div className="space-y-4">
                  {/* Firma bilgileri */}
                  <div className="border border-[#E5E5E5] rounded p-4 bg-[#FAFAFA]">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Firma Bilgileri</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <div><span className="text-neutral-500">Ad:</span> <span className="font-semibold">{preview.company?.name}</span></div>
                      <div><span className="text-neutral-500">Vade:</span> <span className="font-semibold">{preview.company?.payment_terms || "-"}</span></div>
                      <div><span className="text-neutral-500">VD:</span> {preview.company?.tax_office || "-"}</div>
                      <div><span className="text-neutral-500">VD No:</span> <span className="num">{preview.company?.tax_no || "-"}</span></div>
                      <div><span className="text-neutral-500">Yetkili:</span> {preview.company?.contact_person || "-"}</div>
                      <div><span className="text-neutral-500">Telefon:</span> <span className="num">{preview.company?.phone || "-"}</span></div>
                      <div className="md:col-span-2"><span className="text-neutral-500">Mail:</span> {preview.company?.email || "-"}</div>
                      <div className="md:col-span-2"><span className="text-neutral-500">Adres:</span> {preview.company?.address || "-"}</div>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura</div>
                      <div className="num font-bold text-lg">{preview.totals?.invoice_count || 0}</div>
                      <div className="text-[11px] text-neutral-500 num">{formatTL(preview.totals?.invoice_total || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödeme</div>
                      <div className="num font-bold text-lg text-[#16A34A]">{preview.totals?.payment_count || 0}</div>
                      <div className="text-[11px] text-[#16A34A] num">{formatTL(preview.totals?.payment_total || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3 md:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bakiye (Fatura − Ödeme)</div>
                      <div className={`num font-bold text-lg ${Math.abs(preview.totals?.balance || 0) > 1 ? "text-[#F59E0B]" : "text-[#16A34A]"}`}>
                        {formatTL(preview.totals?.balance || 0)}
                      </div>
                    </div>
                  </div>

                  {/* Combined table */}
                  <div className="border border-[#E5E5E5] rounded overflow-hidden max-h-[400px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr>
                          {["Tarih","Tür","Fatura No / Banka","Tutar (₺)"].map((h, i) => (
                            <th key={h} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...(preview.invoices || []).map(x => ({ ...x, _type: "inv" })),
                          ...(preview.payments || []).map(x => ({ ...x, _type: "pay" })),
                        ].sort((a,b) => (a.date || "").localeCompare(b.date || "")).map((r, i) => (
                          <tr key={i} className="border-t border-[#F5F5F5]">
                            <td className="px-2 py-1.5 num">{r.date}</td>
                            <td className="px-2 py-1.5">
                              <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 text-white ${r._type === "inv" ? "bg-[#0020C2]" : "bg-[#16A34A]"}`}>
                                {r._type === "inv" ? "Fatura" : "Ödeme"}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 num text-neutral-700">{r._type === "inv" ? r.invoice_no : r.payment_method}</td>
                            <td className={`px-2 py-1.5 num text-right font-semibold ${r._type === "inv" ? "" : "text-[#16A34A]"}`}>{formatTL(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button data-testid="cari-commit-btn" onClick={runCommit} disabled={loading}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40">
                        {loading ? "Kaydediliyor..." : `✓ Sisteme İşle (${(preview.totals?.invoice_count || 0) + (preview.totals?.payment_count || 0)} kayıt)`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
